import {
	type FrontendPlacementMessage,
	type FrontendStage,
} from '@post-office/message-maker-browser/types';

import { type ComponentType, useEffect, useState } from 'react';

import { noValidation } from '@post-office/message-maker-browser/message-helpers';

import {
	PlacementFrontend,
	type PlacementRenderProps,
} from '@post-office/browser--statuspage/statuspage-create-component-spotlight-placement';

import { useMountToHtmlElement } from '@post-office/components--post-office-team';

import { usePostOfficeUserInteractions } from '@atlassian/post-office-analytics/interactions';

import { useMessageEvent } from '@post-office/message-lifecycle';

import { Text } from '@atlaskit/primitives/compiled';

import { FormattedMessage } from 'react-intl';

import { messages } from '../../../components/messages';

import { type MessageTemplateContext } from '../../../index';

import type { CreatePlacementMessageConfig } from '@post-office/message-template-contracts/placement-v2';

import { SpotlightStepCount } from '@atlaskit/spotlight/step-count';

import { SpotlightSecondaryAction } from '@atlaskit/spotlight/secondary-action';

import { SpotlightPrimaryAction } from '@atlaskit/spotlight/primary-action';

import { SpotlightHeadline } from '@atlaskit/spotlight/headline';

import { SpotlightHeader } from '@atlaskit/spotlight/header';

import { SpotlightFooter } from '@atlaskit/spotlight/footer';

import { SpotlightDismissControl } from '@atlaskit/spotlight/dismiss-control';

import { SpotlightControls } from '@atlaskit/spotlight/controls';

import { SpotlightCard } from '@atlaskit/spotlight/card';

import { SpotlightBody } from '@atlaskit/spotlight/body';

import { SpotlightActions } from '@atlaskit/spotlight/actions';

import { PopoverTarget } from '@atlaskit/spotlight/popover-target';

import { PopoverProvider } from '@atlaskit/spotlight/popover-provider';

import { PopoverContent } from '@atlaskit/spotlight/popover-content';

type RenderProps = MessageTemplateContext & PlacementRenderProps;

type ComponentProps = PlacementRenderProps & { onDismiss?: () => void };

/**
 * DOM selectors for status-page-create to provide when integrating.
 * Step 1 targets add services area, step 2 targets create group area, step 3 targets add more components area.
 */
export const STATUSPAGE_CREATE_COMPONENT_SPOTLIGHT_SELECTOR =
	'#statuspage-create-component-spotlight-target';

export const STATUSPAGE_CREATE_COMPONENT_STEP2_SPOTLIGHT_SELECTOR =
	'#statuspage-create-component-step2-spotlight-target';

export const STATUSPAGE_CREATE_COMPONENT_STEP3_SPOTLIGHT_SELECTOR =
	'#statuspage-create-component-step3-spotlight-target';

const preventAutoDismiss = () => {};

interface FirstStepProps {
	dismiss: () => void | Promise<void>;
	next: () => void | Promise<void>;
}

interface LastStepProps {
	dismiss: () => void | Promise<void>;
	complete: () => void | Promise<void>;
	next: () => void | Promise<void>;
}

const StepContent = ({
	heading,
	body,
	stepLabel,
	onNext,
	onDismiss,
	onComplete,
	isLastStep,
}: {
	body: React.ReactNode;
	heading: React.ReactNode;
	isLastStep: boolean;
	onComplete: () => void;
	onDismiss: () => void;
	onNext: () => void;
	stepLabel: string;
}) => (
	<SpotlightCard>
		<SpotlightHeader>
			<SpotlightHeadline>{heading}</SpotlightHeadline>
			<SpotlightControls>
				<SpotlightDismissControl autoFocus={false} onClick={onDismiss} />
			</SpotlightControls>
		</SpotlightHeader>
		<SpotlightBody>
			<Text>{body}</Text>
		</SpotlightBody>
		<SpotlightFooter>
			<SpotlightStepCount>{stepLabel}</SpotlightStepCount>
			<SpotlightActions>
				<SpotlightSecondaryAction onClick={onDismiss}>
					<FormattedMessage {...messages.createSecondaryAction} />
				</SpotlightSecondaryAction>
				<SpotlightPrimaryAction onClick={isLastStep ? onComplete : onNext}>
					<FormattedMessage
						{...(isLastStep ? messages.detailsGotIt : messages.createPrimaryAction)}
					/>
				</SpotlightPrimaryAction>
			</SpotlightActions>
		</SpotlightFooter>
	</SpotlightCard>
);

const SpotlightCardStep1 = ({ dismiss, next }: FirstStepProps) => (
	<StepContent
		heading={<FormattedMessage {...messages.step1Heading} />}
		body={<FormattedMessage {...messages.step1Body} />}
		stepLabel="1/3"
		onNext={next}
		onDismiss={dismiss}
		onComplete={dismiss}
		isLastStep={false}
	/>
);

const SpotlightCardStep2 = ({ dismiss, next }: FirstStepProps) => (
	<StepContent
		heading={<FormattedMessage {...messages.step2Heading} />}
		body={<FormattedMessage {...messages.step2Body} />}
		stepLabel="2/3"
		onNext={next}
		onDismiss={dismiss}
		onComplete={dismiss}
		isLastStep={false}
	/>
);

const SpotlightCardStep3 = ({ dismiss, complete, next }: LastStepProps) => (
	<StepContent
		heading={<FormattedMessage {...messages.step3Heading} />}
		body={<FormattedMessage {...messages.step3Body} />}
		stepLabel="3/3"
		onNext={next}
		onDismiss={dismiss}
		onComplete={complete}
		isLastStep={true}
	/>
);

const tour: [
	ComponentType<Record<string, never>>,
	ComponentType<FirstStepProps>,
	ComponentType<FirstStepProps>,
	ComponentType<LastStepProps>,
] = [
	() => <></>,
	({ dismiss, next }: FirstStepProps) => (
		<PopoverProvider>
			<PopoverTarget>
				<div />
			</PopoverTarget>
			<PopoverContent dismiss={preventAutoDismiss} placement="right-end" isVisible={true}>
				<SpotlightCardStep1 dismiss={dismiss} next={next} />
			</PopoverContent>
		</PopoverProvider>
	),
	({ dismiss, next }: FirstStepProps) => (
		<PopoverProvider>
			<PopoverTarget>
				<div />
			</PopoverTarget>
			<PopoverContent dismiss={preventAutoDismiss} placement="right-end" isVisible={true}>
				<SpotlightCardStep2 dismiss={dismiss} next={next} />
			</PopoverContent>
		</PopoverProvider>
	),
	({ dismiss, complete, next }: LastStepProps) => (
		<PopoverProvider>
			<PopoverTarget>
				<div />
			</PopoverTarget>
			<PopoverContent dismiss={preventAutoDismiss} placement="right-end" isVisible={true}>
				<SpotlightCardStep3 dismiss={dismiss} complete={complete} next={next} />
			</PopoverContent>
		</PopoverProvider>
	),
];

export const render: {
	type: 'render';
	validate: (input: unknown) => RenderProps;
	Component: (input: PlacementRenderProps) => JSX.Element | null;
} = PlacementFrontend.createFrontendStage({
	type: 'render',
	validate: noValidation<RenderProps>(),
	Component: ({ onDismiss }: ComponentProps) => {
		const { messageDismissed } = useMessageEvent();
		const { onAcknowledged, onClosed, onStepChanged } = usePostOfficeUserInteractions();

		const [step, setStep] = useState<number>(1);
		const [isStep1Visible, setIsStep1Visible] = useState<boolean>(false);
		const [isStep2Visible, setIsStep2Visible] = useState<boolean>(false);
		const [isStep3Visible, setIsStep3Visible] = useState<boolean>(false);

		// Poll for target DOM elements - they may not exist yet when the spotlight mounts
		useEffect(() => {
			if (typeof window === 'undefined') {
				return;
			}

			let intervalId: ReturnType<typeof setInterval> | null = null;

			const checkForElements = () => {
				const step1Element = globalThis.document?.querySelector(
					STATUSPAGE_CREATE_COMPONENT_SPOTLIGHT_SELECTOR,
				);
				const step2Element = globalThis.document?.querySelector(
					STATUSPAGE_CREATE_COMPONENT_STEP2_SPOTLIGHT_SELECTOR,
				);
				const step3Element = globalThis.document?.querySelector(
					STATUSPAGE_CREATE_COMPONENT_STEP3_SPOTLIGHT_SELECTOR,
				);
				if (step1Element) setIsStep1Visible(true);
				if (step2Element) setIsStep2Visible(true);
				if (step3Element) setIsStep3Visible(true);

				// Early exit: stop polling once all elements are found
				if (step1Element && step2Element && step3Element && intervalId) {
					clearInterval(intervalId);
					intervalId = null;
				}
			};

			checkForElements();

			intervalId = setInterval(checkForElements, 100);
			const timeoutId = setTimeout(() => {
				if (intervalId) {
					clearInterval(intervalId);
				}
			}, 5000);

			return () => {
				if (intervalId) clearInterval(intervalId);
				clearTimeout(timeoutId);
			};
		}, []);

		const renderInlineStep1 = useMountToHtmlElement({
			selectors: STATUSPAGE_CREATE_COMPONENT_SPOTLIGHT_SELECTOR,
			as: 'inline',
			position: 'inside',
		});

		const renderInlineStep2 = useMountToHtmlElement({
			selectors: STATUSPAGE_CREATE_COMPONENT_STEP2_SPOTLIGHT_SELECTOR,
			as: 'inline',
			position: 'inside',
		});

		const renderInlineStep3 = useMountToHtmlElement({
			selectors: STATUSPAGE_CREATE_COMPONENT_STEP3_SPOTLIGHT_SELECTOR,
			as: 'inline',
			position: 'inside',
		});

		const dismiss = async () => {
			await messageDismissed();
			void onClosed({ interactionIdentifier: 'dismiss' });
			setStep(0);
			onDismiss?.();
		};

		const complete = async () => {
			await messageDismissed();
			void onAcknowledged({
				interactionIdentifier: 'got-it',
				additionalMetadata: { actionTaken: true },
			});
			setStep(0);
			onDismiss?.();
		};

		const next = () => {
			void onStepChanged({
				interactionIdentifier: 'next',
				direction: 'next',
				fromStep: step,
				toStep: Math.min(tour.length - 1, step + 1),
			});
			setStep(Math.min(tour.length - 1, step + 1));
		};

		const Step1Component = tour[1];
		const Step2Component = tour[2];
		const Step3Component = tour[3];

		if (!isStep1Visible || !isStep2Visible || !isStep3Visible) {
			return null;
		}

		return (
			<div>
				{step === 1 && renderInlineStep1(<Step1Component dismiss={dismiss} next={next} />)}
				{step === 2 && renderInlineStep2(<Step2Component dismiss={dismiss} next={next} />)}
				{step === 3 &&
					renderInlineStep3(<Step3Component dismiss={dismiss} next={next} complete={complete} />)}
			</div>
		);
	},
});

export const MessageFrontend: (config: CreatePlacementMessageConfig) => FrontendPlacementMessage<{
	render: FrontendStage<PlacementRenderProps>;
}> = PlacementFrontend.createMessageFrontend({
	render,
});
